import { ChangeDetectionStrategy, ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IonicModule } from '@ionic/angular';
import { DateTime as LuxonDateTime } from 'luxon';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';

import { Util } from 'src/shared/util/util';
import { readSecondaryPageContext, toIsoDate } from 'src/shared/util/secondary-page-context';

import { DemoBlockService } from 'src/shared/services/demo-block.service';
import { FinancialConnectionsAccount, FinancialConnectionsService } from 'src/shared/services/financial-connections.service';

import { RadioButtonModel } from 'src/tidy-ui-components/components/form/radio-button/radio-button.model';
import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

interface ConnectionHistoryParams {
  /** Every bank linked in one Stripe session; the answer applies to all of them. */
  connections?: FinancialConnectionsAccount[];
  connection?: FinancialConnectionsAccount;
  isNewConnection?: boolean;
}

type RangeChoice = 'link_day' | 'months_3' | 'months_12' | 'all' | 'custom';

/**
 * Sets how far back a bank counts (`track_transactions_from`). Shown right after a
 * bank is linked ("also import past transactions?") and again from Manage Connections.
 */
@Component({
  templateUrl: 'connection-history.page.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, IonicModule, ReactiveFormsModule, TidyUiComponentsModule, SharedModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ConnectionHistoryPage implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly navCtrl = inject(CustomNavController);
  private readonly rightSidePanelService = inject(RightSidePanelService);
  private readonly storage = inject(TidyStorage);
  private readonly util = inject(Util);
  private readonly demoBlock = inject(DemoBlockService);
  private readonly financialConnections = inject(FinancialConnectionsService);

  readonly form = this.fb.group({
    choice: ['link_day' as RangeChoice, Validators.required],
    customDate: [''],
  });

  isRightSideContent = true;
  loaded = false;
  submitted = false;
  saving = false;
  errorMessage = '';
  isNewConnection = false;
  connections: FinancialConnectionsAccount[] = [];
  connectionLabels: string[] = [];
  pageTitle = 'Transaction Start Date';
  showCustomDate = false;
  rangeItems: RadioButtonModel[] = [];

  readonly boundSave = () => this.save();
  readonly boundSkip = () => this.goBack();

  private linkDay = LuxonDateTime.local().toFormat('yyyy-MM-dd');

  async ngOnInit() {
    const { isRightSideContent, params } = await readSecondaryPageContext<ConnectionHistoryParams>(this.storage);
    this.isRightSideContent = isRightSideContent;
    this.connections = params.connections?.length ? params.connections : (params.connection ? [params.connection] : []);
    this.isNewConnection = !!params.isNewConnection;
    this.pageTitle = this.isNewConnection ? 'Import Past Transactions?' : 'Transaction Start Date';
    this.rightSidePanelService.setDialogPageTitle(this.pageTitle);
    if (this.connections.length === 0) {
      this.errorMessage = 'Bank account not found.';
      this.loaded = true;
      this.cdr.markForCheck();
      return;
    }
    this.connectionLabels = this.connections.map(c => `${c.institution_name} ····${c.last4}`);
    const first = this.connections[0];
    this.linkDay = (first.created_at ? LuxonDateTime.fromISO(first.created_at) : LuxonDateTime.local()).toFormat('yyyy-MM-dd');
    this.rangeItems = [
      { value: 'link_day', viewValue: `From ${LuxonDateTime.fromISO(this.linkDay).toFormat('MMM d, yyyy')} (when the bank was linked)` },
      { value: 'months_3', viewValue: 'Last 3 months' },
      { value: 'months_12', viewValue: 'Last 12 months' },
      { value: 'all', viewValue: 'All available history' },
      { value: 'custom', viewValue: 'Choose a date' },
    ];
    this.form.patchValue(this.currentChoice(first));
    this.showCustomDate = this.form.controls.choice.value === 'custom';
    this.form.controls.choice.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(choice => {
        this.showCustomDate = choice === 'custom';
        this.cdr.markForCheck();
      });
    this.loaded = true;
    this.cdr.markForCheck();
  }

  private currentChoice(connection: FinancialConnectionsAccount): { choice: RangeChoice; customDate: string } {
    const from = connection.track_transactions_from;
    if (from === null || from === undefined) { return { choice: 'all', customDate: '' }; }
    if (from === this.linkDay) { return { choice: 'link_day', customDate: '' }; }
    return { choice: 'custom', customDate: from };
  }

  private resolveDate(): string | null | undefined {
    const { choice, customDate } = this.form.getRawValue();
    const today = LuxonDateTime.local();
    switch (choice) {
      case 'link_day': return this.linkDay;
      case 'months_3': return today.minus({ months: 3 }).toFormat('yyyy-MM-dd');
      case 'months_12': return today.minus({ months: 12 }).toFormat('yyyy-MM-dd');
      case 'all': return null;
      case 'custom': return toIsoDate(customDate) || undefined;
      default: return undefined;
    }
  }

  async save(): Promise<void> {
    this.submitted = true;
    if (this.form.invalid || this.saving) { return; }
    const date = this.resolveDate();
    if (date === undefined) {
      this.errorMessage = 'Please choose a date.';
      this.cdr.markForCheck();
      return;
    }
    if (this.demoBlock.block()) { return; }
    this.saving = true;
    this.errorMessage = '';
    this.cdr.markForCheck();
    try {
      await Promise.all(this.connections.map(c => this.financialConnections.updateTrackTransactionsFrom(c.id, date)));
      await this.storage.save('didMakeConnectionChanges', true);
      this.util.showSuccess(date === null
        ? 'Importing all available history. New rows are classified in the background.'
        : 'Start date updated. New rows are classified in the background.');
      this.goBack();
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message || 'Unable to update the start date.';
    } finally {
      this.saving = false;
      this.cdr.markForCheck();
    }
  }

  goBack(): void {
    if (this.isRightSideContent) {
      if (this.rightSidePanelService.historyContext$.getValue().length > 1) {
        this.rightSidePanelService.goBack();
      } else {
        this.rightSidePanelService.closeRightSidePanel();
      }
    } else {
      this.navCtrl.back();
    }
  }
}
